import { useEffect, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { KAFFA } from "@/constants/testIds";
import { useLocale } from "@/contexts/LocaleContext";

const severityClass = {
  info: "border-l-accent",
  success: "border-l-emerald-500",
  warning: "border-l-amber-500",
  critical: "border-l-red-500",
};

export const AlertsBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const { t, formatDateTime } = useLocale();

  const load = async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  const unread = alerts.filter((a) => !a.read).length;

  const markAll = async () => {
    await api.post("/alerts/read-all").catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid={KAFFA.alertsBell}
          className="relative rounded-none h-8 w-8 hover:bg-secondary"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-accent ring-1 ring-background animate-pulse" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={KAFFA.alertsPanel}
        align="end"
        className="rounded-none w-[380px] p-0 border-border"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {t("alerts.label")} {unread > 0 && <span className="text-accent">· {unread} {t("alerts.new")}</span>}
          </div>
          <Button onClick={markAll} variant="ghost" size="sm" className="rounded-none text-xs h-7">
            <CheckCheck className="h-3 w-3 mr-1" />
            {t("alerts.mark_read")}
          </Button>
        </div>
        <ScrollArea className="h-[420px]">
          {alerts.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {t("alerts.empty")}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {alerts.map((a) => (
                <li
                  key={a.id}
                  className={`px-4 py-3 border-l-2 ${severityClass[a.severity] || severityClass.info} ${
                    a.read ? "opacity-60" : ""
                  } hover:bg-secondary/50 transition-colors`}
                >
                  <div className="text-sm font-medium leading-snug">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.message}</div>
                  <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-2">
                    {formatDateTime(a.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
